import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  GridLegacy as Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocalHospital as MedicalIcon,
  Home as HomeIcon,
  ManageAccounts as ManageAccountsIcon,
  Contacts as ContactsIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { User, Organization } from '../types';

interface OrganizationSelectorProps {
  user: User;
  userRole: string;
  onSelectOrganization: (org: Organization | { id: string; name: string }) => void;
}

const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({ user, userRole, onSelectOrganization }) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5010'}/api/organizations`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      
      // Handle wrapped response {success: true, organizations: [...]}
      const orgs = Array.isArray(data) ? data : (data.organizations || []);
      
      // Filter to show only child organizations (exclude master org "Soniq CX")
      // Master org has parent_organization_id = null
      const childOrgs = orgs.filter(
        (org: any) => org.parent_organization_id !== null && org.parent_organization_id !== undefined
      );
      
      console.log('✅ Filtered child organizations:', childOrgs);
      
      // For superusers/master users, show all child organizations
      setOrganizations(childOrgs);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const getOrgIcon = (orgName: string) => {
    if (orgName.toLowerCase().includes('med')) {
      return <MedicalIcon sx={{ fontSize: 60 }} />;
    } else if (orgName.toLowerCase().includes('home')) {
      return <HomeIcon sx={{ fontSize: 60 }} />;
    }
    return <BusinessIcon sx={{ fontSize: 60 }} />;
  };

  const handleOrgClick = (org: Organization) => {
    onSelectOrganization(org);
  };

  const handleAdminModuleClick = (moduleId: string, moduleName: string) => {
    // For admin modules, set a special "org" object
    // App.tsx will handle navigation via window.location in handleSelectOrg
    onSelectOrganization({ id: moduleId, name: moduleName });
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box 
        sx={{
          p: 4,
          mb: 4,
          background: 'linear-gradient(135deg, #2C3E50 0%, #4B88A2 100%)',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <BusinessIcon sx={{ fontSize: 48 }} />
          <Box>
            <Typography variant="h4" fontWeight="600">
              Welcome, {user.username}!
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Select an organization to get started
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Organizations Section */}
      <Box sx={{ mb: 5 }}>
        <Typography
          variant="h5"
          fontWeight="600"
          sx={{ mb: 3, color: '#2C3E50', display: 'flex', alignItems: 'center', gap: 1 }}
        >
          Organizations
          <Chip
            label={organizations.length}
            size="small"
            sx={{
              bgcolor: '#4B88A2',
              color: 'white',
              fontWeight: 600,
            }}
          />
        </Typography>

        <Grid container spacing={3}>
          {organizations.map((org) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={org.id}>
              <Card 
                elevation={0}
                sx={{
                  height: '100%',
                  background: '#ffffff',
                  border: '2px solid #0C0C0C',
                  borderRadius: '12px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 8px 24px rgba(12, 12, 12, 0.25)',
                    border: '2px solid #4B88A2',
                    background: '#F8F9FA',
                  },
                }}
              >
                <CardActionArea 
                  onClick={() => handleOrgClick(org)}
                  sx={{ 
                    height: '100%',
                    borderRadius: '16px'
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', py: 5, px: 3 }}>
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        margin: '0 auto 24px',
                        borderRadius: '20px',
                        bgcolor: '#4B88A2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 16px rgba(75, 136, 162, 0.25)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        }
                      }}
                    >
                      <Box sx={{ color: '#F8F9FA' }}>
                        {getOrgIcon(org.name)}
                      </Box>
                    </Box>
                    <Typography 
                      variant="h5" 
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        color: '#0C0C0C',
                        mb: 1,
                        letterSpacing: '-0.3px'
                      }}
                    >
                      {org.name}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{
                        color: '#4B88A2',
                        fontWeight: 400
                      }}
                    >
                      Click to access portal
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {organizations.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ color: '#4B88A2' }}>
              No organizations available
            </Typography>
          </Box>
        )}
      </Box>

      {/* Administration Section - For users with special access */}
      {(user.user_management_access || userRole === 'superuser' || user.is_master) && (
        <Box sx={{ mb: 5 }}>
          <Typography
            variant="h5"
            fontWeight="600"
            sx={{ mb: 3, color: '#2C3E50', display: 'flex', alignItems: 'center', gap: 1 }}
          >
            Administration
            <Chip
              label={(user.user_management_access ? 1 : 0) + ((userRole === 'superuser' || user.is_master) ? 2 : 0)}
              size="small"
              sx={{
                bgcolor: '#d32f2f',
                color: 'white',
                fontWeight: 600,
              }}
            />
          </Typography>

          <Grid container spacing={3}>
            {/* User Management */}
            {user.user_management_access && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Card
                  onClick={() => handleAdminModuleClick('user-management', 'User Management')}
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6,
                      '& .module-icon': {
                        transform: 'scale(1.1)',
                        color: '#d32f2f',
                      },
                    },
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Box
                      className="module-icon"
                      sx={{
                        color: '#d32f2f',
                        mb: 2,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <ManageAccountsIcon sx={{ fontSize: 48 }} />
                    </Box>
                    <Typography
                      variant="h6"
                      fontWeight="600"
                      sx={{ mb: 1, color: '#2C3E50' }}
                    >
                      User Management
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minHeight: 40 }}
                    >
                      Manage users and permissions
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Module Permissions - Superusers only */}
            {(userRole === 'superuser' || user.is_master) && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Card
                  onClick={() => handleAdminModuleClick('module-permissions', 'Module Permissions')}
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6,
                      '& .module-icon': {
                        transform: 'scale(1.1)',
                        color: '#9c27b0',
                      },
                    },
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Box
                      className="module-icon"
                      sx={{
                        color: '#9c27b0',
                        mb: 2,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <SecurityIcon sx={{ fontSize: 48 }} />
                    </Box>
                    <Typography
                      variant="h6"
                      fontWeight="600"
                      sx={{ mb: 1, color: '#2C3E50' }}
                    >
                      Module Permissions
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minHeight: 40 }}
                    >
                      Manage roles and module access
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* CRM - Superusers only */}
            {(userRole === 'superuser' || user.is_master) && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Card
                  onClick={() => handleAdminModuleClick('crm', 'CRM')}
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6,
                      '& .module-icon': {
                        transform: 'scale(1.1)',
                        color: '#00bcd4',
                      },
                    },
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Box
                      className="module-icon"
                      sx={{
                        color: '#00bcd4',
                        mb: 2,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <ContactsIcon sx={{ fontSize: 48 }} />
                    </Box>
                    <Typography
                      variant="h6"
                      fontWeight="600"
                      sx={{ mb: 1, color: '#2C3E50' }}
                    >
                      CRM
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minHeight: 40 }}
                    >
                      Manage leads, contacts, and accounts
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </Container>
  );
};

export default OrganizationSelector;
